/**
 * Knowledge Base Detail Page
 * 
 * Shows KB details with tabs for collections management and settings.
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Icon,
  SearchInput,
  Skeleton,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useKnowledgeBase } from '@/hooks/use-knowledge-bases';
import { useCollections, useUpdateCollection } from '@/hooks/use-knowledge';
import { KnowledgeBaseModal } from '@/features/knowledge/components/KnowledgeBaseModal';

// =============================================================================
// Status Badge
// =============================================================================

function CollectionStatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { color: string; label: string }> = {
    active: { color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', label: 'Active' },
    processing: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', label: 'Processing' },
    error: { color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', label: 'Error' },
    archived: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: 'Archived' },
  };
  const config = statusConfig[status] || statusConfig.active;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

// =============================================================================
// Link Existing Collection Dialog
// =============================================================================

function LinkExistingCollectionDialog({
  open,
  onOpenChange,
  knowledgeBaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  knowledgeBaseId: string;
}) {
  const { t } = useI18n();
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const updateCollectionMutation = useUpdateCollection();

  // Fetch unlinked collections with search
  const unlinkedQuery = useCollections({
    page: 1,
    page_size: 50,
    knowledge_base_id: '__none__',
    ...(search ? { search } : {}),
  });

  const unlinkedCollections = unlinkedQuery.data?.data ?? [];

  const handleLink = useCallback(async (collectionId: string) => {
    setLinkingId(collectionId);
    try {
      await updateCollectionMutation.mutateAsync({
        id: collectionId,
        knowledge_base_id: knowledgeBaseId,
      });
      toast.success(t('admin.knowledge.knowledgeBaseDetail.collectionLinked'));
      onOpenChange(false);
    } catch {
      toast.error(t('admin.knowledge.knowledgeBaseDetail.collectionLinkError'));
    } finally {
      setLinkingId(null);
    }
  }, [knowledgeBaseId, updateCollectionMutation, t, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setSearch(''); }}>
      <DialogContent className="sm:max-w-2xl max-h-[70vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t('admin.knowledge.knowledgeBaseDetail.linkDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('admin.knowledge.knowledgeBaseDetail.linkDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="mb-3">
          <SearchInput
            placeholder={t('admin.knowledge.knowledgeBaseDetail.linkDialog.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {unlinkedQuery.isLoading && (
            <div className="space-y-2 p-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          )}

          {!unlinkedQuery.isLoading && unlinkedCollections.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="folder-open" size={32} className="mx-auto mb-2 opacity-50" />
              <p>{t('admin.knowledge.knowledgeBaseDetail.linkDialog.noAvailable')}</p>
            </div>
          )}

          {!unlinkedQuery.isLoading && unlinkedCollections.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.knowledge.knowledgeBaseDetail.linkDialog.colName')}</TableHead>
                  <TableHead>{t('admin.knowledge.knowledgeBaseDetail.linkDialog.colMode')}</TableHead>
                  <TableHead className="text-right">{t('admin.knowledge.knowledgeBaseDetail.linkDialog.colDocs')}</TableHead>
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {unlinkedCollections.map((col) => (
                  <TableRow key={col.id}>
                    <TableCell className="font-medium">{col.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {col.mode === 'rag' ? 'RAG' : 'Full Document'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {col.document_count}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleLink(col.id)}
                        disabled={linkingId !== null}
                      >
                        {linkingId === col.id ? (
                          <Icon name="loader" size={14} className="animate-spin" />
                        ) : (
                          t('admin.knowledge.knowledgeBaseDetail.linkDialog.link')
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Collections Tab Content
// =============================================================================

function CollectionsTab({ knowledgeBaseId }: { knowledgeBaseId: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [collectionToRemove, setCollectionToRemove] = useState<{ id: string; name: string } | null>(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const collectionsQuery = useCollections({
    page: currentPage,
    page_size: 20,
    knowledge_base_id: knowledgeBaseId,
  });

  const updateCollectionMutation = useUpdateCollection();

  const collections = collectionsQuery.data?.data ?? [];
  const pagination = collectionsQuery.data?.meta;

  const handleRemoveFromKB = useCallback(async () => {
    if (!collectionToRemove) return;
    try {
      await updateCollectionMutation.mutateAsync({
        id: collectionToRemove.id,
        knowledge_base_id: '',
      });
      toast.success(t('admin.knowledge.knowledgeBaseDetail.collectionRemoved'));
      setCollectionToRemove(null);
    } catch {
      toast.error(t('admin.knowledge.knowledgeBaseDetail.collectionRemoveError'));
    }
  }, [collectionToRemove, updateCollectionMutation, t]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="max-w-sm flex-1">
          <SearchInput
            placeholder={t('admin.knowledge.knowledgeBaseDetail.searchCollections')}
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
            onClear={() => { setSearchText(''); setCurrentPage(1); }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            leftIcon="link"
            onClick={() => setShowLinkDialog(true)}
          >
            {t('admin.knowledge.knowledgeBaseDetail.linkExisting')}
          </Button>
          <Button
            leftIcon="plus"
            onClick={() => navigate(`/knowledge/collections/new?kb=${knowledgeBaseId}`)}
          >
            {t('admin.knowledge.knowledgeBaseDetail.createNew')}
          </Button>
        </div>
      </div>

      {/* Loading */}
      {collectionsQuery.isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1">
                  <Skeleton className="h-5 w-48 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty */}
      {!collectionsQuery.isLoading && collections.length === 0 && (
        <Card className="p-8 text-center">
          <Icon name="folder-open" size={48} className="mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {t('admin.knowledge.knowledgeBaseDetail.noCollections')}
          </h3>
          <p className="text-muted-foreground mb-4">
            {t('admin.knowledge.knowledgeBaseDetail.noCollectionsHint')}
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              leftIcon="link"
              onClick={() => setShowLinkDialog(true)}
            >
              {t('admin.knowledge.knowledgeBaseDetail.linkExisting')}
            </Button>
            <Button
              leftIcon="plus"
              onClick={() => navigate(`/knowledge/collections/new?kb=${knowledgeBaseId}`)}
            >
              {t('admin.knowledge.knowledgeBaseDetail.createNew')}
            </Button>
          </div>
        </Card>
      )}

      {/* Collection List */}
      {!collectionsQuery.isLoading && collections.length > 0 && (
        <div className="space-y-3">
          {collections.map((col) => (
            <Card
              key={col.id}
              className="p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/knowledge/collections/${col.id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 shrink-0">
                    <Icon name="file-text" size={20} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-medium truncate">{col.name}</h4>
                    <p className="text-sm text-muted-foreground truncate">
                      {col.description || t('admin.knowledge.knowledgeBaseDetail.noDescription')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0 ml-4">
                  <div className="text-right text-sm text-muted-foreground hidden sm:block">
                    <div>{col.document_count} docs</div>
                    <div>{col.total_chunks} chunks</div>
                  </div>
                  <CollectionStatusBadge status={col.status} />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Icon name="more-vertical" size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/knowledge/collections/${col.id}`);
                      }}>
                        <Icon name="eye" size={14} className="mr-2" />
                        {t('admin.knowledge.knowledgeBaseDetail.viewCollection')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/knowledge/collections/${col.id}/ingest`);
                      }}>
                        <Icon name="upload" size={14} className="mr-2" />
                        {t('admin.knowledge.knowledgeBaseDetail.uploadDocuments')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setCollectionToRemove({ id: col.id, name: col.name });
                        }}
                      >
                        <Icon name="x" size={14} className="mr-2" />
                        {t('admin.knowledge.knowledgeBaseDetail.removeFromKB')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </Card>
          ))}

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <nav className="flex items-center justify-center gap-3 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                {t('common.previous')}
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                {currentPage} / {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= pagination.total_pages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                {t('common.next')}
              </Button>
            </nav>
          )}
        </div>
      )}

      {/* Remove from KB Confirmation */}
      <AlertDialog
        open={!!collectionToRemove}
        onOpenChange={(open) => { if (!open) setCollectionToRemove(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.knowledge.knowledgeBaseDetail.removeDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.knowledge.knowledgeBaseDetail.removeDialog.description', {
                name: collectionToRemove?.name ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveFromKB}>
              {t('admin.knowledge.knowledgeBaseDetail.removeDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link Existing Collection Dialog */}
      <LinkExistingCollectionDialog
        open={showLinkDialog}
        onOpenChange={setShowLinkDialog}
        knowledgeBaseId={knowledgeBaseId}
      />
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function KnowledgeBaseDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('collections');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const kbQuery = useKnowledgeBase(id ?? '');
  const kb = kbQuery.data;

  if (!id) {
    navigate('/knowledge/bases');
    return null;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/knowledge/bases')}
        >
          <Icon name="arrow-left" size={20} />
        </Button>

        {kbQuery.isLoading ? (
          <div>
            <Skeleton className="h-7 w-48 mb-1" />
            <Skeleton className="h-5 w-64" />
          </div>
        ) : kb ? (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground truncate">{kb.name}</h1>
              <Badge variant="outline" className="shrink-0">
                {kb.scope_type}
              </Badge>
              {!kb.is_active && (
                <Badge variant="secondary" className="shrink-0">
                  {t('admin.knowledge.knowledgeBaseDetail.inactive')}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1 truncate">
              {kb.description || t('admin.knowledge.knowledgeBaseDetail.noDescription')}
            </p>
          </div>
        ) : null}

        {kb && (
          <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
            <Icon name="edit" size={16} className="mr-2" />
            {t('common.edit')}
          </Button>
        )}
      </div>

      {/* Error */}
      {kbQuery.isError && (
        <Card className="p-8 text-center">
          <Icon name="alert-circle" size={48} className="mx-auto text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {t('admin.knowledge.knowledgeBaseDetail.loadError')}
          </h3>
          <Button variant="outline" onClick={() => kbQuery.refetch()}>
            {t('common.retry')}
          </Button>
        </Card>
      )}

      {/* Tabs */}
      {kb && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="collections">
              <Icon name="folder-open" size={16} className="mr-2" />
              {t('admin.knowledge.knowledgeBaseDetail.tabs.collections')}
              <Badge variant="secondary" className="ml-2 text-xs">
                {kb.collection_count}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Icon name="settings" size={16} className="mr-2" />
              {t('admin.knowledge.knowledgeBaseDetail.tabs.settings')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="collections">
            <CollectionsTab knowledgeBaseId={id} />
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.knowledge.knowledgeBaseDetail.settingsTitle')}</CardTitle>
                <CardDescription>
                  {t('admin.knowledge.knowledgeBaseDetail.settingsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('admin.knowledge.knowledgeBaseDetail.scopeType')}
                    </label>
                    <p className="text-sm capitalize">{kb.scope_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('admin.knowledge.knowledgeBaseDetail.status')}
                    </label>
                    <p className="text-sm">{kb.is_active ? t('common.active') : t('common.inactive')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('admin.knowledge.knowledgeBaseDetail.created')}
                    </label>
                    <p className="text-sm">{new Date(kb.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('admin.knowledge.knowledgeBaseDetail.updated')}
                    </label>
                    <p className="text-sm">{new Date(kb.updated_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Edit Modal */}
      {kb && (
        <KnowledgeBaseModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          knowledgeBase={kb}
        />
      )}
    </div>
  );
}
